import React, { useContext, useEffect, useMemo, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import { featureCollection } from "@turf/helpers";
import { area as turfArea, bbox as turfBbox, booleanIntersects, intersect } from "@turf/turf";
import { Spinner } from "react-bootstrap";
import type { GeoJSONSource } from "maplibre-gl";
import { searchSingleLayerForConflicts } from "src/components/ConflictSearchCalculations";
import { actionSetLayerHierarchy, MapitStateActionType, MapitStateContext } from "src/states/MapitState";
import { KeyValueLine, KeyValuesHorizontal, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import "../../css/propertyInfoStyle.css";
import { addDisplayLayer, removeDisplayLayer } from "src/utils/mapLayerHelpers";
import { formatPercentage, getJPBFieldLayer, DEFAULT_JPB_FIELD_LAYER } from "./JPBFunc";
import { getCropCode, isFieldOutsideRotation } from "./jpbFieldClasses";
import { isMarkvandingWell } from "./markvandingWell";
import { LayerHierarchy, LayerHierarchyEndNode, LayerInfo } from "src/common/managers/Types";
import { OldHierarchy } from "src/managers/OldHierarchy";
import { Persistence } from "src/managers/Persistence";
import { message } from "src/components/Message";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

const DISPLAY_SOURCE_ID = "jpb-mini-conflict-results";

type MiniConflictGroup =
   | "bnbo"
   | "fields"
   | "drinkingWater"
   | "planning"
   | "retention"
   | "wells";

type MiniConflictLayer = {
   id: string;
   group: MiniConflictGroup;
   source: string;
   layer: string;
   title: string;
   subgroup?: string;
   filterFeature?: (feature: any) => boolean;
   resultNote?: (features: any[]) => string | null;
   search?: (searchGeometry: any, layer: MiniConflictLayer) => Promise<any>;
};

type MiniConflictResult = MiniConflictLayer & {
   conflict: any | null;
   err: string;
   matchedCount: number;
   progress: number;
   rawMatchedCount: number;
};

const MINI_CONFLICT_LAYER_TEMPLATES: MiniConflictLayer[] = [
   {
      id: "bnbo-areas",
      group: "bnbo",
      source: "Miljogis",
      layer: "bnbo",
      title: "BNBO areas on property"
   },
   {
      id: "fields-outside-rotation",
      group: "fields",
      source: "FVM (Føde, Land og Fisk)",
      layer: DEFAULT_JPB_FIELD_LAYER,
      title: "Fields outside crop rotation",
      filterFeature: isFieldOutsideRotation,
      resultNote: cropCodeResultNote
   },
   {
      id: "field-section-3-overlap",
      group: "fields",
      source: "Miljø Portal",
      layer: "2013 Beskyttede naturtyper",
      title: "Field parts overlapping §3 protected nature",
      search: searchFieldSection3Overlap,
      resultNote: section3ResultNote
   },
   {
      id: "sgo-areas",
      group: "drinkingWater",
      source: "Miljogis",
      layer: "saarbaregrundvandsdannendeomraader",
      title: "SGO (vulnerable groundwater recharge areas)"
   },
   {
      id: "action-areas-sfi",
      group: "drinkingWater",
      source: "Miljogis",
      layer: "indsatsomraader",
      title: "Action areas within pesticide-sensitive abstraction areas",
      subgroup: "Action areas, adopted (MST)",
      filterFeature: isSfiActionArea
   },
   {
      id: "action-areas-nfi",
      group: "drinkingWater",
      source: "Miljogis",
      layer: "indsatsomraader",
      title: "Action areas within nitrate-sensitive abstraction areas",
      subgroup: "Action areas, adopted (MST)",
      filterFeature: isNfiActionArea
   },
   {
      id: "valuable-agricultural-areas-adopted",
      group: "planning",
      source: "Plandata.dk",
      layer: "theme_pdk_vaerdifuldtlandbrugsomraade_vedtaget",
      title: "Specially valuable agricultural areas, adopted (§11 a, 9)"
   },
   {
      id: "retention-below-40",
      group: "retention",
      source: "Miljogis",
      layer: "kvaelstofretention",
      title: "Retention below 40%",
      filterFeature: isRetentionBelow40,
      resultNote: retentionResultNote,
      search: searchRetention
   },
   {
      id: "jupiter-irrigation-wells",
      group: "wells",
      source: "Geus",
      layer: "Danske Boringer",
      title: "Jupiter wells with irrigation",
      filterFeature: isMarkvandingWell
   }
];

const SOURCE_ALIASES: Record<string, string> = {
   Miljogis: "Miljøgis"
};

function getMiniConflictLayers(): MiniConflictLayer[] {
   const fieldLayer = getJPBFieldLayer();
   return MINI_CONFLICT_LAYER_TEMPLATES.map((layer) =>
      layer.id === "fields-outside-rotation"
         ? { ...layer, layer: fieldLayer }
         : layer
   );
}

const CROP_NAME_PROPERTY_CANDIDATES = [
   "Afgroede",
   "Afgrøde",
   "afgroede",
   "afgrøde",
   "cropName"
];

const SECTION3_TYPE_PROPERTY_CANDIDATES = [
   "Naturtype",
   "naturtype",
   "NATURTYPE",
   "arealtype",
   "Arealtype",
   "ArealType",
   "kategori",
   "Kategori",
   "type",
   "Type"
];

const SECTION3_TYPE_LABELS: Record<string, string> = {
   eng: "Eng",
   hede: "Hede",
   mose: "Mose",
   overdrev: "Overdrev",
   strandeng: "Strandeng",
   "strand eng": "Strandeng",
   soe: "Sø",
   so: "Sø",
   sø: "Sø"
};

function initialResults(): MiniConflictResult[] {
   return getMiniConflictLayers().map((layer) => ({
      ...layer,
      conflict: null,
      err: "",
      matchedCount: -1,
      progress: 0,
      rawMatchedCount: -1
   }));
}

function toSearchFeatureCollection(geometry: any): any | null {
   if (!geometry) return null;
   if (geometry.type === "FeatureCollection") return geometry;
   if (geometry.type === "Feature") return featureCollection([geometry]);
   if (geometry.type) {
      return featureCollection([
         {
            type: "Feature",
            geometry,
            properties: {}
         } as any
      ]);
   }
   return null;
}

function normalizeFeatureCollection(value: any): any {
   if (value?.type === "FeatureCollection") return value;
   return featureCollection([]);
}

function matchedCountFromFeatureCollection(value: any): number {
   const metadataCount = parseInt(value?.metadata?.numberMatched ?? "", 10);
   if (Number.isFinite(metadataCount)) return metadataCount;
   return Array.isArray(value?.features) ? value.features.length : 0;
}

function withFilteredFeatures(value: any, features: any[], matchedCount: number): any {
   return {
      ...value,
      type: "FeatureCollection",
      features,
      metadata: {
         ...value?.metadata,
         numberMatched: String(matchedCount),
         numberReturned: features.length
      }
   };
}

function normalizeText(value: unknown): string {
   return String(value ?? "").trim().toLowerCase();
}

function getPropertyCaseInsensitive(properties: Record<string, unknown>, key: string): unknown {
   const direct = properties[key];
   if (direct !== undefined) return direct;
   const lowerKey = key.toLowerCase();
   const realKey = Object.keys(properties).find((propertyKey) => propertyKey.toLowerCase() === lowerKey);
   return realKey ? properties[realKey] : undefined;
}

function getFirstPropertyCaseInsensitive(properties: Record<string, unknown>, keys: string[]): unknown {
   for (const key of keys) {
      const value = getPropertyCaseInsensitive(properties, key);
      if (value !== undefined && value !== null && value !== "") return value;
   }
   return undefined;
}

function getCropName(feature: any): string | null {
   const properties = feature?.properties ?? {};
   const value = getFirstPropertyCaseInsensitive(properties, CROP_NAME_PROPERTY_CANDIDATES);
   const name = String(value ?? "").trim();
   return name || null;
}

function cropCodeResultNote(features: any[]): string | null {
   const labels = Array.from(new Set(features
      .map((feature) => {
         const code = getCropCode(feature);
         if (!code) return null;
         const name = getCropName(feature);
         return name ? `${code} ${name}` : code;
      })
      .filter((value): value is string => !!value)
   )).slice(0, 3);

   if (!labels.length) return null;
   return labels.join(", ") + (features.length > labels.length ? " ..." : "");
}

function normalizeSection3Type(value: unknown): string | null {
   const text = String(value ?? "").trim();
   if (!text) return null;
   const normalized = text.toLowerCase();
   const matchingKey = Object.keys(SECTION3_TYPE_LABELS).find((key) => normalized.includes(key));
   return matchingKey ? SECTION3_TYPE_LABELS[matchingKey] : text;
}

function getSection3Type(feature: any): string | null {
   const properties = feature?.properties ?? {};
   const candidateValue = getFirstPropertyCaseInsensitive(properties, SECTION3_TYPE_PROPERTY_CANDIDATES);
   const candidateType = normalizeSection3Type(candidateValue);
   if (candidateType) return candidateType;

   for (const value of Object.values(properties)) {
      const type = normalizeSection3Type(value);
      if (type && Object.values(SECTION3_TYPE_LABELS).includes(type)) return type;
   }

   return null;
}

function section3ResultNote(features: any[]): string | null {
   const labels = Array.from(new Set(features
      .map(getSection3Type)
      .filter((value): value is string => !!value)
   ));

   if (!labels.length) return null;
   return labels.join(", ");
}

function parseNumber(value: unknown): number | null {
   if (typeof value === "number" && Number.isFinite(value)) return value;
   if (typeof value !== "string") return null;
   const normalized = value.replace("%", "").replace(",", ".").trim();
   const match = normalized.match(/-?\d+(\.\d+)?/);
   if (!match) return null;
   const parsed = Number(match[0]);
   return Number.isFinite(parsed) ? parsed : null;
}

const RETENTION_PROPERTY_CANDIDATES = [
   "ret_tot",
   "retention",
   "retention_pct",
   "retention_procent",
   "ret_pct",
   "retentionpct"
];

function getRetentionPercent(feature: any): number | null {
   const properties = feature?.properties ?? {};
   for (const key of RETENTION_PROPERTY_CANDIDATES) {
      const value = parseNumber(getPropertyCaseInsensitive(properties, key));
      if (value !== null) return value <= 1 ? value * 100 : value;
   }

   const fallbackKey = Object.keys(properties).find((key) => key.toLowerCase().includes("ret"));
   if (!fallbackKey) return null;

   const value = parseNumber(properties[fallbackKey]);
   if (value === null) return null;
   return value <= 1 ? value * 100 : value;
}

function isRetentionBelow40(feature: any): boolean {
   const retentionPercent = getRetentionPercent(feature);
   return retentionPercent !== null && retentionPercent < 40;
}

function retentionResultNote(features: any[]): string | null {
   const values = features
      .map(getRetentionPercent)
      .filter((value): value is number => value !== null && Number.isFinite(value));
   if (!values.length) return null;
   return `${Localization.getText("Lowest retention")}: ${formatPercentage(Math.min(...values))}`;
}

function getIndsatsomraadeKategori(feature: any): string {
   const properties = feature?.properties ?? {};
   return normalizeText(getPropertyCaseInsensitive(properties, "kategori"));
}

function isSfiActionArea(feature: any): boolean {
   return getIndsatsomraadeKategori(feature) === "sfi";
}

function isNfiActionArea(feature: any): boolean {
   return getIndsatsomraadeKategori(feature) === "nfi";
}

function getFeatures(geometry: any): any[] {
   if (!geometry) return [];
   if (geometry.type === "Feature") return [geometry];
   if (geometry.type === "FeatureCollection" && Array.isArray(geometry.features)) {
      return geometry.features;
   }
   if (geometry.type) {
      return [{
         type: "Feature",
         geometry,
         properties: {}
      }];
   }
   return [];
}

function intersectTwoFeatures(first: any, second: any): any | null {
   try {
      return intersect(featureCollection([first, second]) as any) as any;
   } catch {
      return null;
   }
}

function getPositiveAreaIntersections(feature: any, clippingFeatures: any[]): any[] {
   return clippingFeatures
      .map((clippingFeature) => intersectTwoFeatures(feature, clippingFeature))
      .filter((intersection): intersection is any => !!intersection?.geometry && turfArea(intersection as any) > 0);
}

async function searchRetention(searchGeometry: any): Promise<any> {
   const searchBbox = turfBbox(searchGeometry);
   const url = new URL("https://wfs2-miljoegis.mim.dk/wfs");
   url.searchParams.set("REQUEST", "GetFeature");
   url.searchParams.set("service", "WFS");
   url.searchParams.set("VERSION", "2.0.0");
   url.searchParams.set("TYPENAMES", "kvaelstofretention");
   url.searchParams.set("outputFormat", "json");
   url.searchParams.set("SRSNAME", "EPSG:4326");
   url.searchParams.set("BBOX", `${searchBbox.join(",")},EPSG:4326`);

   const response = await fetch(url.toString());
   if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
   }

   const result = await response.json();
   const searchFeatures = getFeatures(searchGeometry);
   const features = (Array.isArray(result?.features) ? result.features : []).filter((feature: any) =>
      feature?.geometry && searchFeatures.some((searchFeature) =>
         searchFeature?.geometry && booleanIntersects(feature, searchFeature)
      )
   );

   return {
      type: "FeatureCollection",
      features,
      metadata: {
         ...result?.metadata,
         numberMatched: String(features.length),
         numberReturned: features.length,
         feature: "kvaelstofretention",
         endpoint: "Miljøgis"
      }
   };
}

async function searchFieldSection3Overlap(searchGeometry: any): Promise<any> {
   const [fieldsResult, section3Result] = await Promise.all([
      searchSingleLayerForConflicts(
         searchGeometry,
         "FVM (Føde, Land og Fisk)",
         getJPBFieldLayer()
      ),
      searchSingleLayerForConflicts(
         searchGeometry,
         "Miljø Portal",
         "2013 Beskyttede naturtyper"
      )
   ]);

   const propertyFeatures = getFeatures(searchGeometry);
   const fieldFeatures = Array.isArray(fieldsResult?.features) ? fieldsResult.features : [];
   const section3Features = Array.isArray(section3Result?.features) ? section3Result.features : [];
   const overlapFeatures: any[] = [];
   const fieldPartsWithinProperty: any[] = [];
   const section3PartsWithinProperty: any[] = [];

   for (const fieldFeature of fieldFeatures) {
      if (!fieldFeature?.geometry) continue;
      const fieldAsFeature = { type: "Feature", geometry: fieldFeature.geometry, properties: fieldFeature.properties ?? {} };
      fieldPartsWithinProperty.push(...(propertyFeatures.length
         ? getPositiveAreaIntersections(fieldAsFeature, propertyFeatures).map((fieldPart) => ({
            ...fieldPart,
            properties: {
               Markblok: fieldFeature.properties?.Markblok,
               Marknr: fieldFeature.properties?.Marknr,
               Afgkode: getCropCode(fieldFeature) ?? fieldFeature.properties?.Afgkode,
               Afgroede: getCropName(fieldFeature) ?? fieldFeature.properties?.Afgroede
            }
         }))
         : [fieldAsFeature]
      ));
   }

   for (const section3Feature of section3Features) {
      if (!section3Feature?.geometry) continue;
      const section3AsFeature = { type: "Feature", geometry: section3Feature.geometry, properties: section3Feature.properties ?? {} };
      const section3Parts = propertyFeatures.length
         ? getPositiveAreaIntersections(section3AsFeature, propertyFeatures)
         : [section3AsFeature];

      section3PartsWithinProperty.push(...section3Parts.map((section3Part) => ({
         ...section3Part,
         properties: {
            ...section3Feature.properties,
            Section3Type: getSection3Type(section3Feature)
         }
      })));
   }

   for (const section3Part of section3PartsWithinProperty) {
      for (const fieldPart of fieldPartsWithinProperty) {
         const overlap = intersectTwoFeatures(section3Part, fieldPart);
         if (!overlap?.geometry || turfArea(overlap as any) <= 0) continue;

         overlapFeatures.push({
            ...overlap,
            properties: {
               ...section3Part.properties,
               ...fieldPart.properties
            }
         });
      }
   }

   return {
      type: "FeatureCollection",
      features: overlapFeatures,
      metadata: {
         ...section3Result?.metadata,
         numberMatched: String(overlapFeatures.length),
         numberReturned: overlapFeatures.length
      }
   };
}

function getGroupTitle(group: MiniConflictGroup): string {
   switch (group) {
      case "bnbo":
         return Localization.getText("BNBO");
      case "fields":
         return Localization.getText("Fields");
      case "drinkingWater":
         return Localization.getText("Groundwater and drinking water");
      case "planning":
         return Localization.getText("Planning");
      case "retention":
         return Localization.getText("Retention");
      case "wells":
         return Localization.getText("Wells");
   }
}

function getResultNote(result: MiniConflictResult): string | null {
   if (!result.progress || result.err || !result.conflict?.features?.length) return null;
   return result.resultNote?.(result.conflict.features) ?? null;
}

function isAddableConflictResult(result: MiniConflictResult): boolean {
   return !result.err && result.matchedCount > 0 && !!result.conflict?.features?.length;
}

function applyMiniConflictLayerDefaults(layerInfo: LayerInfo, result: MiniConflictResult): LayerInfo {
   if (result.id !== "fields-outside-rotation") {
      return layerInfo;
   }

   return {
      ...layerInfo,
      styling: {
         ...layerInfo.styling,
         textByProperty: ["Afgroede"]
      }
   };
}

async function prepareMiniConflictLayersForPlot(results: MiniConflictResult[]): Promise<LayerInfo[]> {
   const layerResults = await Promise.allSettled(
      results.filter(isAddableConflictResult).map(async (result) => {
         const fc = result.conflict;
         if (!fc?.features?.length) {
            throw new Error("No conflict data");
         }

         return await new Promise<LayerInfo>((resolve) => {
            Persistence.createLayersFromGeoJSON(
               Localization.getText(result.title),
               fc as {},
               "",
               (layerInfo) => resolve(applyMiniConflictLayerDefaults(layerInfo, result))
            );
         });
      })
   );

   return layerResults
      .filter((result): result is PromiseFulfilledResult<LayerInfo> => result.status === "fulfilled")
      .map((result) => result.value);
}

function groupResults(results: MiniConflictResult[]): Array<[MiniConflictGroup, MiniConflictResult[]]> {
   const order: MiniConflictGroup[] = ["bnbo", "fields", "drinkingWater", "planning", "retention", "wells"];
   return order
      .map((group): [MiniConflictGroup, MiniConflictResult[]] => [
         group,
         results.filter((result) => result.group === group)
      ])
      .filter(([, groupResults]) => groupResults.length > 0);
}

type ConflictRowBlock =
   | { type: "item"; result: MiniConflictResult }
   | { type: "subgroup"; title: string; items: MiniConflictResult[] };

function groupRowsIntoBlocks(rows: MiniConflictResult[]): ConflictRowBlock[] {
   const blocks: ConflictRowBlock[] = [];
   for (const row of rows) {
      if (!row.subgroup) {
         blocks.push({ type: "item", result: row });
         continue;
      }

      const last = blocks[blocks.length - 1];
      if (last?.type === "subgroup" && last.title === row.subgroup) {
         last.items.push(row);
      } else {
         blocks.push({ type: "subgroup", title: row.subgroup, items: [row] });
      }
   }
   return blocks;
}

function subgroupLetter(index: number): string {
   return String.fromCharCode(97 + index);
}

function JPBMiniConflictItem(props: {
   result: MiniConflictResult;
   selected: boolean;
   label: string;
   nested?: boolean;
   onTogglePreview: (result: MiniConflictResult) => void;
}) {
   const { result, selected, label, nested, onTogglePreview } = props;
   const note = !result.err ? getResultNote(result) : null;
   return (
      <div
         className={`ConflictItem${nested ? " jpb-mini-conflict-item--nested" : ""}`}
         onClick={() => onTogglePreview(result)}
      >
         <div>
            <div className={selected ? "ConflictItemSelected" : ""}>
               {label}
            </div>
            {note && (
               <div className="ConflictItemSubnote">{note}</div>
            )}
         </div>
         {
            result.err
               ? <div className="ConflictItemError">{Localization.getText("Error")}</div>
               : (
                  <div className="ConflictItemDetails">
                     {!result.progress ? <Spinner animation="border" size="sm" /> : result.matchedCount}
                  </div>
               )
         }
      </div>
   );
}

function JPBMiniConflictSourceGroup(props: {
   groupTitle: string;
   rows: MiniConflictResult[];
   selectedResultId: string | null;
   onTogglePreview: (result: MiniConflictResult) => void;
}) {
   const { groupTitle, rows, selectedResultId, onTogglePreview } = props;
   const blocks = groupRowsIntoBlocks(rows);
   return (
      <div className="ConflictSourceGroup">
         <div className="ConflictSourceGroupHeader">
            <div className="ConflictSourceGroupTitle"><b>{groupTitle}</b> </div>
            <span>{Localization.getText("Conflicts")}</span>
         </div>
         <div className="ConflictSourceGroupContent">
            {blocks.map((block) => {
               if (block.type === "item") {
                  return (
                     <JPBMiniConflictItem
                        key={block.result.id}
                        result={block.result}
                        selected={selectedResultId === block.result.id}
                        label={Localization.getText(block.result.title)}
                        onTogglePreview={onTogglePreview}
                     />
                  );
               }

               return (
                  <div key={block.title} className="jpb-mini-conflict-subgroup">
                     <div className="jpb-mini-conflict-subgroup-title">
                        {Localization.getText(block.title)}
                     </div>
                     {block.items.map((result, index) => (
                        <JPBMiniConflictItem
                           key={result.id}
                           result={result}
                           selected={selectedResultId === result.id}
                           nested
                           label={`${subgroupLetter(index)}) ${Localization.getText(result.title)}`}
                           onTogglePreview={onTogglePreview}
                        />
                     ))}
                  </div>
               );
            })}
         </div>
      </div>
   );
}

async function searchLayer(
   searchGeometry: any,
   layer: MiniConflictLayer,
   rawResultCache?: Map<string, any>
): Promise<MiniConflictResult> {
   const cacheKey = `${SOURCE_ALIASES[layer.source] ?? layer.source}::${layer.layer}`;
   let rawResult: any;
   if (layer.search) {
      rawResult = normalizeFeatureCollection(await layer.search(searchGeometry, layer));
   } else if (rawResultCache?.has(cacheKey)) {
      rawResult = rawResultCache.get(cacheKey);
   } else {
      rawResult = normalizeFeatureCollection(
         await searchSingleLayerForConflicts(
            searchGeometry,
            (SOURCE_ALIASES[layer.source] ?? layer.source) as any,
            layer.layer
         )
      );
      rawResultCache?.set(cacheKey, rawResult);
   }
   const rawMatchedCount = matchedCountFromFeatureCollection(rawResult);
   const rawFeatures = Array.isArray(rawResult.features) ? rawResult.features : [];
   const filteredFeatures = layer.filterFeature
      ? rawFeatures.filter(layer.filterFeature)
      : rawFeatures;
   const matchedCount = layer.filterFeature ? filteredFeatures.length : rawMatchedCount;

   return {
      ...layer,
      conflict: withFilteredFeatures(rawResult, filteredFeatures, matchedCount),
      err: "",
      matchedCount,
      progress: 1,
      rawMatchedCount
   };
}

export function JPBMiniConflictSearch(props: { propertyGeometry?: any }) {
   const { state: mapitState, dispatch: mapitDispatch } = useContext(MapitStateContext);
   const [results, setResults] = useState<MiniConflictResult[]>(() => initialResults());
   const [selectedResultId, setSelectedResultId] = useState<string | null>(null);

   const geometryKey = useMemo(() => {
      if (!props.propertyGeometry) return "";
      try {
         return JSON.stringify(props.propertyGeometry);
      } catch {
         return `${Date.now()}`;
      }
   }, [props.propertyGeometry]);

   const searchGeometry = useMemo(
      () => toSearchFeatureCollection(props.propertyGeometry),
      [geometryKey, props.propertyGeometry]
   );

   const totalMatches = results.reduce((sum, result) => sum + Math.max(0, result.matchedCount), 0);
   const searchesCompleted = results.reduce((sum, result) => sum + result.progress, 0);
   const isSearching = !!searchGeometry && searchesCompleted < results.length;
   const failedSearches = results.filter((result) => result.err).length;

   const conflictResultsToAdd = useMemo(
      () => results.filter(isAddableConflictResult),
      [results]
   );

   const canAddConflictLayers = conflictResultsToAdd.length > 0 && !isSearching;

   const groupedResults = useMemo(() => groupResults(results), [results]);
   const sourcesCount = groupedResults.length;

   useEffect(() => {
      const map = mapitState.map?.getMapPop();
      if (!map) return;
      if (!map.getSource(DISPLAY_SOURCE_ID)) {
         addDisplayLayer(map, DISPLAY_SOURCE_ID);
      }
      return () => {
         try {
            if (map.getSource(DISPLAY_SOURCE_ID)) {
               removeDisplayLayer(map, DISPLAY_SOURCE_ID);
            }
         } catch (error) {
            console.debug("JPB mini conflict cleanup failed", error);
         }
      };
   }, [mapitState.map]);

   useEffect(() => {
      const map = mapitState.map?.getMapPop();
      const source = map?.getSource(DISPLAY_SOURCE_ID) as GeoJSONSource | undefined;
      if (source) {
         source.setData(featureCollection([]) as any);
      }
      setSelectedResultId(null);
   }, [geometryKey, mapitState.map]);

   useEffect(() => {
      let cancelled = false;

      if (!searchGeometry) {
         setResults(initialResults());
         return () => {
            cancelled = true;
         };
      }

      setResults(initialResults());

      const groupedBySource = getMiniConflictLayers().reduce<Record<string, MiniConflictLayer[]>>((groups, layer) => {
         const source = layer.source;
         groups[source] ??= [];
         groups[source].push(layer);
         return groups;
      }, {});
      const rawResultCache = new Map<string, any>();

      Promise.all(
         Object.values(groupedBySource).map(async (layers) => {
            for (const layer of layers) {
               let nextResult: MiniConflictResult;
               try {
                  nextResult = await searchLayer(searchGeometry, layer, rawResultCache);
               } catch (error) {
                  nextResult = {
                     ...layer,
                     conflict: null,
                     err: error instanceof Error ? error.message : String(error),
                     matchedCount: -1,
                     progress: 1,
                     rawMatchedCount: -1
                  };
               }

               if (!cancelled) {
                  setResults((current) =>
                     current.map((result) => result.id === nextResult.id ? nextResult : result)
                  );
               }
            }
         })
      ).catch((error) => {
         console.error("JPB mini conflict search failed", error);
      });

      return () => {
         cancelled = true;
      };
   }, [searchGeometry]);

   function previewResult(result: MiniConflictResult) {
      const map = mapitState.map?.getMapPop();
      const source = map?.getSource(DISPLAY_SOURCE_ID) as GeoJSONSource | undefined;
      if (!source || result.conflict == null) return;

      if (selectedResultId === result.id) {
         source.setData(featureCollection([]) as any);
         setSelectedResultId(null);
         return;
      }

      source.setData(result.conflict);
      setSelectedResultId(result.id);
   }

   const title = (
      <div className="jpb-mini-conflict-title">
         <span>{Localization.getText("Mini conflict search")}</span>
         <span className="jpb-mini-conflict-title__status">
            {!searchGeometry
               ? Localization.getText("Pending geometry")
               : isSearching
                  ? Localization.getText("Searching...")
                  : Localization.getFormattedText("{count} findings", { count: totalMatches })}
         </span>
      </div>
   );

   if (!searchGeometry) {
      return null;
   }

   return (
      <div className="jpb-mini-conflict-search" data-jpb-conflict-search={isSearching ? "searching" : "ready"}>
         <details className="jpb-analyzer-details jpb-analyzer-details--flush">
            <summary>{title}</summary>
            <div className="jpb-mini-conflict-content">
               <div className="jpb-mini-conflict-kv-toolbar" aria-live="polite">
                  <KeyValuesHorizontal>
                     <KeyValueLine valKey={Localization.getText("Total searches")} value={searchesCompleted} />
                     <KeyValueLine valKey={Localization.getText("Conflicts found")} value={totalMatches} />
                     <KeyValueLine valKey={Localization.getText("Searches failed")} value={failedSearches} />
                     <KeyValueLine valKey={Localization.getText("Sources")} value={sourcesCount} />
                  </KeyValuesHorizontal>
                  <ViewButtonIcon
                     disabled={!canAddConflictLayers}
                     title={
                        canAddConflictLayers
                           ? Localization.getFormattedText("Add all {count} conflict results as map layers.", {
                              count: conflictResultsToAdd.length
                           })
                           : Localization.getText("No conflicts to add as map layers.")
                     }
                     onClick={async () => {
                        if (!canAddConflictLayers) {
                           return;
                        }

                        try {
                           const previouslyLayerHierarchy = structuredClone(mapitState.layerHierarchy) as LayerHierarchy;
                           const lowestId = OldHierarchy.extractLowestId(mapitState.layerHierarchy);
                           const layers = await prepareMiniConflictLayersForPlot(conflictResultsToAdd);
                           if (!layers.length) {
                              return;
                           }

                           const addedLayers = await Promise.all(layers.map((layer) => {
                              return new Promise<LayerInfo>((resolve) => {
                                 mapitDispatch({
                                    type: MapitStateActionType.AddDataLayer,
                                    payload: { layerInfo: layer },
                                    callbacks: {
                                       onLayerAdded: (li) => resolve(li)
                                    }
                                 });
                              });
                           }));

                           previouslyLayerHierarchy.children.push({
                              id: lowestId - 1,
                              isCollapsed: true,
                              name: Localization.getText("Conflict search"),
                              children: addedLayers.map((layer): LayerHierarchyEndNode => ({
                                 id: layer.layerId
                              }))
                           });
                           mapitDispatch(actionSetLayerHierarchy(previouslyLayerHierarchy));
                        } catch (error) {
                           message.error(error instanceof Error ? error.message : String(error));
                        }
                     }}
                  >
                     <MIBIcon icon="addToMap" />
                  </ViewButtonIcon>
               </div>

               <div className="ConflictSearchScrollContainer jpb-mini-conflict-scroll">
                  <div className="jpb-mini-conflict-groups">
                     {groupedResults.map(([group, groupItems]) => (
                        <JPBMiniConflictSourceGroup
                           key={group}
                           groupTitle={getGroupTitle(group)}
                           rows={groupItems}
                           selectedResultId={selectedResultId}
                           onTogglePreview={previewResult}
                        />
                     ))}
                  </div>
               </div>

               {!isSearching && totalMatches === 0 && failedSearches === 0 && (
                  <div className="jpb-mini-conflict-empty">
                     {Localization.getText("No conflicts found for this property")}
                  </div>
               )}
            </div>
         </details>
      </div>
   );
}
